import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Plus, Edit2, Trash2, X, Briefcase, BookOpen } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const BASE_GRADES = [
    "1ro Primaria", "2do Primaria", "3ro Primaria", "4to Primaria", "5to Primaria", "6to Primaria",
    "1ro Secundaria", "2do Secundaria", "3ro Secundaria", "4to Secundaria", "5to Secundaria",
];

const GRADE_SECTIONS = BASE_GRADES.flatMap((g) =>
    (g === "5to Secundaria" ? ["A", "B", "C"] : ["A", "B"]).map((s) => `${g} ${s}`)
);

const COLORS = ["#1B2F6E", "#F5A623", "#10B981", "#3B82F6", "#EF4444", "#8B5CF6", "#EC4899"];

export default function MisCursos() {
    const [courses, setCourses] = useState([]);
    const [creating, setCreating] = useState(false);
    const [editing, setEditing] = useState(null);

    async function load() {
        const r = await api.get("/courses");
        setCourses(r.data || []);
    }
    useEffect(() => { load(); }, []);

    async function remove(id) {
        if (!window.confirm("¿Eliminar curso?")) return;
        await api.delete(`/courses/${id}`);
        toast.success("Curso eliminado");
        load();
    }

    return (
        <div className="p-6 md:p-10 fade-in" data-testid="mis-cursos-page">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-3xl font-semibold inline-flex items-center gap-2">
                        <Briefcase size={26} className="text-edu-navy" /> Mis Cursos
                    </h1>
                    <p className="text-edu-body">Crea cursos y asígnalos a secciones específicas.</p>
                </div>
                <button
                    onClick={() => setCreating(true)}
                    className="edu-btn-primary px-4 py-2.5 text-sm inline-flex items-center gap-2"
                    data-testid="create-course-button"
                >
                    <Plus size={14} /> Nuevo Curso
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {courses.length === 0 && (
                    <div className="col-span-full edu-card p-10 text-center text-edu-muted">
                        <BookOpen size={36} className="mx-auto mb-2 opacity-50" />
                        Aún no has creado cursos.
                    </div>
                )}
                {courses.map((c) => (
                    <div
                        key={c.id}
                        className="edu-card p-6 relative overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
                        onClick={() => window.location.href = `/profesor/cursos/${c.id}`}
                    >
                        <div className="absolute top-0 left-0 right-0 h-1.5" style={{ background: c.color }} />
                        <h3 className="text-lg font-semibold mb-1 mt-2">{c.name}</h3>
                        <p className="text-sm text-edu-body mb-3 line-clamp-3">{c.description || "Sin descripción"}</p>
                        <div className="flex flex-wrap gap-1.5 mb-4">
                            {(c.sections || []).map((s) => <span key={s} className="edu-pill bg-edu-navy/10 text-edu-navy">{s}</span>)}
                        </div>
                        <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                            <button onClick={() => setEditing(c)} className="px-3 py-1.5 text-xs rounded-lg border border-edu hover:bg-gray-50 inline-flex items-center gap-1">
                                <Edit2 size={12} /> Editar
                            </button>
                            <button onClick={() => remove(c.id)} className="px-3 py-1.5 text-xs rounded-lg bg-red-100 text-red-700 inline-flex items-center gap-1">
                                <Trash2 size={12} /> Eliminar
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {(creating || editing) && (
                <CourseModal
                    course={editing}
                    onClose={() => { setCreating(false); setEditing(null); }}
                    onSaved={() => { setCreating(false); setEditing(null); load(); }}
                />
            )}
        </div>
    );
}

function CourseModal({ course, onClose, onSaved }) {
    const isEdit = !!course;
    const [form, setForm] = useState(course ?? { name: "", description: "", sections: [], color: COLORS[0] });
    const [saving, setSaving] = useState(false);
    const [selectedBase, setSelectedBase] = useState("");

    function toggleSection(s) {
        setForm((f) => ({
            ...f,
            sections: f.sections.includes(s) ? f.sections.filter((x) => x !== s) : [...f.sections, s],
        }));
    }

    const filteredGradeSections = selectedBase ? GRADE_SECTIONS.filter((g) => g.startsWith(selectedBase)) : GRADE_SECTIONS;

    async function save() {
        setSaving(true);
        try {
            isEdit ? await api.patch(`/courses/${course.id}`, form) : await api.post("/courses", form);
            toast.success("Guardado");
            onSaved();
        } catch { toast.error("Error al guardar"); }
        finally { setSaving(false); }
    }

    return createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl p-6 max-w-2xl w-full max-h-[85vh] flex flex-col shadow-xl m-auto text-slate-900 dark:text-white" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4 flex-shrink-0">
                    <h3 className="text-xl font-semibold">{isEdit ? "Editar curso" : "Nuevo curso"}</h3>
                    <button onClick={onClose} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full text-slate-400 dark:text-slate-400"><X size={20} /></button>
                </div>

                <div className="overflow-y-auto flex-1 pr-2 space-y-4 custom-scrollbar">
                    <input placeholder="Nombre del curso" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 w-full p-3 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none" />
                    <textarea placeholder="Descripción" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 w-full p-3 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none" />

                    <div>
                        <span className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400 font-semibold mb-2 block">Secciones asignadas</span>
                        <div className="flex flex-wrap gap-2 mb-3">
                            <button type="button" onClick={() => setSelectedBase("")} className={`px-3 py-1.5 text-xs rounded-full border ${selectedBase === "" ? "bg-indigo-600 border-indigo-600 text-white" : "border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200"}`}>Todos</button>
                            {BASE_GRADES.map((g) => (
                                <button key={g} type="button" onClick={() => setSelectedBase(g)} className={`px-3 py-1.5 text-xs rounded-full border ${selectedBase === g ? "bg-indigo-600 border-indigo-600 text-white" : "border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200"}`}>{g}</button>
                            ))}
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {filteredGradeSections.map((gs) => (
                                <button key={gs} type="button" onClick={() => toggleSection(gs)} className={`px-3 py-1.5 text-xs rounded-full border ${form.sections.includes(gs) ? "bg-indigo-600 border-indigo-600 text-white" : "border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200"}`}>{gs}</button>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 mt-4 border-t border-slate-200 dark:border-slate-700 flex-shrink-0">
                    <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800">Cancelar</button>
                    <button onClick={save} disabled={saving || !form.name} className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 px-4 py-2 text-sm rounded-xl text-white font-medium">{saving ? "Guardando…" : "Guardar"}</button>
                </div>
            </div>
        </div>,
        document.body
    );
}
